import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Button } from '../components/ui/button';
import {
  Download,
  Loader2,
  FileText,
  FolderArchive,
  Shield,
  CheckCircle2,
  AlertCircle,
  Leaf,
  FileImage,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';

const DataExportPage = () => {
  const navigate = useNavigate();
  const {
    currentOrg,
    user,
    isAdmin,
    canPerform
  } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ step: '', percent: 0 });

  // Data counts for preview
  const [dataCounts, setDataCounts] = useState({
    emissions: 0,
    offsets: 0,
    documents: 0,
    reports: 0
  });

  useEffect(() => {
    if (currentOrg && !canPerform('SETTINGS_DATA_EXPORT') && !isAdmin) {
      toast.error('Only organization owners and full access members can export data');
      navigate('/dashboard');
    }
  }, [currentOrg, canPerform, isAdmin, navigate]);

  const fetchDataCounts = useCallback(async () => {
    if (!currentOrg?.id) return;

    try {
      // Count emissions
      const { count: emissionsCount } = await supabase
        .from('emissions_data')
        .select('*', { count: 'exact', head: true })
        .eq('organisation_id', currentOrg.id);

      // Count offsets
      const { count: offsetsCount } = await supabase
        .from('carbon_offsets')
        .select('*', { count: 'exact', head: true })
        .eq('organisation_id', currentOrg.id);

      // Count documents
      const { count: documentsCount } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('organisation_id', currentOrg.id);

      // Count reports
      const { count: reportsCount } = await supabase
        .from('report_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('organisation_id', currentOrg.id)
        .eq('status', 'completed');

      setDataCounts({
        emissions: emissionsCount || 0,
        offsets: offsetsCount || 0,
        documents: documentsCount || 0,
        reports: reportsCount || 0
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data counts:', error);
      setLoading(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg) {
      fetchDataCounts();
    }
  }, [currentOrg, fetchDataCounts]);

  // Main export function
  const handleExportAll = async () => {
    if (!currentOrg?.id) return;

    setExporting(true);
    const zip = new JSZip();

    try {
      // Step 1: Export Emissions Data
      setExportProgress({ step: 'Fetching emissions data...', percent: 10 });
      const { data: emissions } = await supabase
        .from('emissions_data')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('entry_date', { ascending: false });

      if (emissions && emissions.length > 0) {
        const emissionsCsv = convertToCSV(emissions, [
          'id', 'category', 'quantity', 'unit', 'co2_equivalent',
          'entry_date', 'financial_year', 'notes', 'factor_value_used', 'factor_source', 'created_at'
        ]);
        zip.file('emissions_data.csv', emissionsCsv);
      }

      // Step 2: Export Carbon Offsets
      setExportProgress({ step: 'Fetching carbon offsets...', percent: 20 });
      const { data: offsets } = await supabase
        .from('carbon_offsets')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('purchase_date', { ascending: false });

      if (offsets && offsets.length > 0) {
        const offsetsCsv = convertToCSV(offsets, [
          'id', 'project_name', 'project_type', 'quantity', 'unit_price',
          'total_cost', 'currency', 'purchase_date', 'registry', 'serial_number', 'created_at'
        ]);
        zip.file('carbon_offsets.csv', offsetsCsv);
      }

      // Step 3: Export Offset Allocations
      setExportProgress({ step: 'Fetching offset allocations...', percent: 30 });
      const { data: allocations } = await supabase
        .from('offset_allocations')
        .select('*')
        .eq('organisation_id', currentOrg.id);

      if (allocations && allocations.length > 0) {
        const allocationsCsv = convertToCSV(allocations, [
          'id', 'offset_id', 'quantity_allocated', 'financial_year', 'allocation_date', 'created_at'
        ]);
        zip.file('offset_allocations.csv', allocationsCsv);
      }

      // Step 4: Export Entry History (Audit Trail)
      setExportProgress({ step: 'Fetching entry history...', percent: 40 });
      const { data: entryHistory } = await supabase
        .from('entry_history')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false });

      if (entryHistory && entryHistory.length > 0) {
        const historyCsv = convertToCSV(entryHistory, [
          'id', 'emission_entry_id', 'action', 'user_email', 'category',
          'quantity', 'unit', 'co2_equivalent', 'notes', 'created_at'
        ]);
        zip.file('entry_history.csv', historyCsv);
      }

      // Step 5: Export Audit Logs
      setExportProgress({ step: 'Fetching audit logs...', percent: 50 });
      const { data: auditLogs } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false });

      if (auditLogs && auditLogs.length > 0) {
        const auditCsv = convertToCSV(auditLogs, [
          'id', 'user_email', 'action', 'resource_type', 'resource_id',
          'description', 'created_at'
        ]);
        zip.file('audit_logs.csv', auditCsv);
      }

      // Step 6: Download Evidence Files
      setExportProgress({ step: 'Downloading evidence files...', percent: 60 });
      const { data: documents } = await supabase
        .from('documents')
        .select('*')
        .eq('organisation_id', currentOrg.id);

      if (documents && documents.length > 0) {
        const evidenceFolder = zip.folder('evidence');
        let downloadedCount = 0;

        for (const doc of documents) {
          if (doc.storage_path) {
            try {
              const { data: signedUrlData } = await supabase.storage
                .from('evidence-vault')
                .createSignedUrl(doc.storage_path, 300);

              if (signedUrlData?.signedUrl) {
                const response = await fetch(signedUrlData.signedUrl);
                if (response.ok) {
                  const blob = await response.blob();
                  const fileName = doc.filename || `evidence_${doc.id}`;
                  evidenceFolder.file(fileName, blob);
                  downloadedCount++;
                }
              }
            } catch (err) {
              console.warn(`Failed to download evidence: ${doc.filename}`, err);
            }
          }
          setExportProgress({
            step: `Downloading evidence files... (${downloadedCount}/${documents.length})`,
            percent: 60 + Math.round((downloadedCount / documents.length) * 15)
          });
        }
      }

      // Step 7: Download Reports
      setExportProgress({ step: 'Downloading reports...', percent: 80 });
      const { data: reports } = await supabase
        .from('report_jobs')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .eq('status', 'completed');

      if (reports && reports.length > 0) {
        const reportsFolder = zip.folder('reports');
        let downloadedReports = 0;

        for (const report of reports) {
          if (report.storage_path) {
            try {
              const { data: signedUrlData } = await supabase.storage
                .from('reports')
                .createSignedUrl(report.storage_path, 300);

              if (signedUrlData?.signedUrl) {
                const response = await fetch(signedUrlData.signedUrl);
                if (response.ok) {
                  const blob = await response.blob();
                  const fileName = `report_FY${report.financial_year}_${report.id.slice(0, 8)}.pdf`;
                  reportsFolder.file(fileName, blob);
                  downloadedReports++;
                }
              }
            } catch (err) {
              console.warn(`Failed to download report: ${report.id}`, err);
            }
          }
          setExportProgress({
            step: `Downloading reports... (${downloadedReports}/${reports.length})`,
            percent: 80 + Math.round((downloadedReports / reports.length) * 15)
          });
        }
      }

      // Step 8: Add README
      setExportProgress({ step: 'Creating archive...', percent: 95 });
      const readme = `# ${currentOrg.name} - Data Export
      
Exported on: ${new Date().toLocaleString('en-AU')}
Organisation ID: ${currentOrg.id}

## Contents

- emissions_data.csv - All emission entries
- carbon_offsets.csv - Carbon offset purchases
- offset_allocations.csv - Offset retirement allocations
- entry_history.csv - Entry change history
- audit_logs.csv - Activity audit log
- evidence/ - Uploaded evidence files (receipts, invoices)
- reports/ - Generated PDF reports

## Data Summary

- Emissions Entries: ${dataCounts.emissions}
- Carbon Offsets: ${dataCounts.offsets}
- Evidence Files: ${dataCounts.documents}
- Reports: ${dataCounts.reports}

---
Exported from Lucent Sky ESG Platform
`;
      zip.file('README.txt', readme);

      // Generate and download ZIP
      setExportProgress({ step: 'Generating ZIP file...', percent: 98 });
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      const fileName = `${currentOrg.name?.replace(/\s+/g, '-')}-data-export-${new Date().toISOString().split('T')[0]}.zip`;
      saveAs(content, fileName);

      // Log the export to audit_logs
      try {
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: user?.id, // Should use internal ID but this is a reasonable fallback
          user_email: user?.email,
          organisation_id: currentOrg.id,
          resource_type: 'data_export',
          resource_id: currentOrg.id,
          action: 'export',
          description: `Full data export: ${dataCounts.emissions} emissions, ${dataCounts.offsets} offsets, ${dataCounts.documents} evidence files, ${dataCounts.reports} reports`,
          new_data: JSON.stringify({
            filename: fileName,
            emissions_count: dataCounts.emissions,
            offsets_count: dataCounts.offsets,
            documents_count: dataCounts.documents,
            reports_count: dataCounts.reports,
            total_items: totalItems
          })
        });
      } catch (auditErr) {
        console.error('Failed to log export:', auditErr);
      }

      setExportProgress({ step: 'Complete!', percent: 100 });
      toast.success('Data export completed successfully!');

    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
      setTimeout(() => setExportProgress({ step: '', percent: 0 }), 2000);
    }
  };

  // Helper function to convert array to CSV
  const convertToCSV = (data, columns) => {
    const headers = columns.join(',');
    const rows = data.map(row => {
      return columns.map(col => {
        let value = row[col];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') value = JSON.stringify(value);
        // Escape quotes and wrap in quotes if contains comma or newline
        value = String(value).replace(/"/g, '""');
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          value = `"${value}"`;
        }
        return value;
      }).join(',');
    });
    return [headers, ...rows].join('\n');
  };

  const totalItems = dataCounts.emissions + dataCounts.offsets + dataCounts.documents + dataCounts.reports;

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <FolderArchive className="w-7 h-7 mr-3 text-[#74A57F]" />
              Data Export & Audit
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Download your organization's environmental data for external reporting
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Permission Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Secure Data Export</h3>
            <p className="text-sm text-blue-600 mt-1">
              This export includes all your organization's emissions data, carbon offsets, evidence files, and reports.
              Only organization owners and full access members can perform this export.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
          </div>
        ) : (
          <>
            {/* Data Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <FileSpreadsheet className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#003B5C]">{dataCounts.emissions}</p>
                    <p className="text-xs text-slate-500">Emissions</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Leaf className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#003B5C]">{dataCounts.offsets}</p>
                    <p className="text-xs text-slate-500">Offsets</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileImage className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#003B5C]">{dataCounts.documents}</p>
                    <p className="text-xs text-slate-500">Evidence Files</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#003B5C]">{dataCounts.reports}</p>
                    <p className="text-xs text-slate-500">Reports</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Export Section */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gradient-to-br from-[#003B5C] to-[#00527a] rounded-xl">
                    <FolderArchive className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-[#003B5C]">Full Data Export</h2>
                    <p className="text-sm text-slate-500">Download all organizational data as a ZIP file</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Emissions & Carbon Data</p>
                      <p className="text-sm text-slate-500">All emissions entries, carbon offsets, and allocations (CSV format)</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Evidence & Attachments</p>
                      <p className="text-sm text-slate-500">All uploaded receipts, invoices, and supporting documents</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Generated Reports</p>
                      <p className="text-sm text-slate-500">All PDF reports generated for your organization</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-700">Audit Trail</p>
                      <p className="text-sm text-slate-500">Entry history and activity logs for compliance</p>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {exporting && (
                  <div className="mt-6 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{exportProgress.step}</span>
                      <span className="font-medium text-[#003B5C]">{exportProgress.percent}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-[#003B5C] to-[#74A57F] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${exportProgress.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Export Button */}
                <div className="mt-6">
                  <Button
                    onClick={handleExportAll}
                    disabled={exporting || totalItems === 0}
                    className="w-full bg-gradient-to-r from-[#003B5C] to-[#00527a] hover:from-[#002a42] hover:to-[#003B5C] text-white py-6 text-lg"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Download All Data ({totalItems} items)
                      </>
                    )}
                  </Button>

                  {totalItems === 0 && (
                    <div className="mt-4 flex items-center justify-center space-x-2 text-amber-600">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">No data available to export</span>
                    </div>
                  )}
                </div>

                {/* Privacy Notice */}
                <p className="mt-4 text-xs text-slate-400 text-center">
                  The ZIP file is generated in your browser and downloaded directly.
                  No data is stored on our servers during this process.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataExportPage;
